/**
 * Analytics — Google Analytics 4 + Facebook Pixel
 *
 * Loads tags only if env IDs configured. Safe to call functions
 * even when not configured (no-op).
 */

const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined;
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

let initialized = false;

export function initAnalytics() {
  if (initialized) return;
  initialized = true;

  // ─── GA4 ────────────────────────────────────────────────
  if (GA4_ID) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: unknown[]) { window.dataLayer!.push(args); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { send_page_view: false });
  }

  // ─── Facebook Pixel ─────────────────────────────────────
  if (FB_PIXEL_ID) {
    /* eslint-disable */
    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq?.('init', FB_PIXEL_ID);
  }
}

export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (window.gtag) window.gtag('event', eventName, params ?? {});
  if (window.fbq) window.fbq('trackCustom', eventName, params ?? {});
}

export function trackPageView(path: string, title?: string) {
  if (window.gtag) {
    window.gtag('event', 'page_view', { page_path: path, page_title: title });
  }
  if (window.fbq) window.fbq('track', 'PageView');
}

export function trackRegister(channel: string, campaign?: string | null) {
  trackEvent('sign_up', { method: 'line_liff', channel, campaign });
  if (window.fbq) window.fbq('track', 'CompleteRegistration', { channel, campaign });
}

// ───────────────────────── GTM DataLayer events ─────────────────────────
// These are the events GTM expects per the Line CRM Feedback doc.

/** Hash a value with SHA-256 (used for PII like phone/email — ad platform safe form) */
async function sha256(value: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(value.trim().toLowerCase());
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return ''; }
}

function pushDataLayer(event: Record<string, unknown>) {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(event);
}

export interface LeadSignupPayload {
  lead_id: string;
  budget_range: string;
  signup_value: number;
  currency?: string;
  source_channel: string;
  phone?: string;
  email?: string;
}

export async function trackLeadSignup(p: LeadSignupPayload) {
  const [phone_sha256, email_sha256] = await Promise.all([
    p.phone ? sha256(p.phone) : Promise.resolve(''),
    p.email ? sha256(p.email) : Promise.resolve(''),
  ]);
  pushDataLayer({
    event: 'lead_signup',
    lead_id: p.lead_id,
    budget_range: p.budget_range,
    signup_value: p.signup_value,
    currency: p.currency ?? 'THB',
    source_channel: p.source_channel,
    phone_sha256,
    email_sha256,
  });
}

export interface UserLoginPayload {
  user_id: string;
  line_user_id: string;
  user_status?: 'new' | 'returning';
  budget_range?: string | null;
  has_purchased?: boolean;
  lifetime_value?: number;
  member_tier?: string;
  phone?: string;
  email?: string;
}

export async function trackUserLogin(p: UserLoginPayload) {
  const [phone_sha256, email_sha256] = await Promise.all([
    p.phone ? sha256(p.phone) : Promise.resolve(''),
    p.email ? sha256(p.email) : Promise.resolve(''),
  ]);
  pushDataLayer({
    event: 'user_login',
    user_id: p.user_id,
    external_id: p.user_id,
    line_user_id: p.line_user_id,
    user_status: p.user_status ?? 'returning',
    budget_range: p.budget_range ?? null,
    has_purchased: p.has_purchased ?? false,
    lifetime_value: p.lifetime_value ?? 0,
    currency: 'THB',
    member_tier: p.member_tier ?? 'Crystal',
    phone_sha256, email_sha256,
    phone: p.phone, email: p.email,
  });
}

// ── E-commerce events (GA4-compatible) ─────────────────────────────
export interface CartItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
}

export function trackViewItem(item: CartItem) {
  pushDataLayer({
    event: 'view_item',
    currency: 'THB',
    value: item.price,
    items: [item],
  });
}

export function trackAddToCart(item: CartItem) {
  pushDataLayer({
    event: 'add_to_cart',
    currency: 'THB',
    value: item.price * item.quantity,
    items: [item],
  });
}

export function trackBeginCheckout(items: CartItem[], total: number) {
  pushDataLayer({
    event: 'begin_checkout',
    currency: 'THB',
    value: total,
    items,
  });
}

export function trackPurchase(orderId: string, items: CartItem[], total: number) {
  pushDataLayer({
    event: 'purchase',
    transaction_id: orderId,
    currency: 'THB',
    value: total,
    items,
  });
}

export function trackRedeemPoints(rewardId: string, points: number) {
  pushDataLayer({ event: 'redeem_points', reward_id: rewardId, points });
}

export function trackUseCoupon(couponCode: string, discount: number) {
  pushDataLayer({ event: 'use_coupon', coupon_code: couponCode, discount, currency: 'THB' });
}
